import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink } from "react-router-dom";
import { Icon } from "@iconify/react";
import searchFill from "@iconify/icons-eva/search-fill";
import filterFill from "@iconify/icons-eva/funnel-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import addFill from "@iconify/icons-eva/plus-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { fDateTime } from "src/utils/formatTime";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import TimePeriodSelector from "src/components/common/TimePeriodSelector";

// ---------------------------------------------------------------------------
// Filter helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Unit formatters
// ---------------------------------------------------------------------------

const formatEnergy = (kwh) => {
  const val = parseFloat(kwh) || 0;
  if (val >= 1000) return { value: (val / 1000).toFixed(2), unit: "MWh" };
  return { value: val.toFixed(2), unit: "kWh" };
};

const formatAmount = (amount) => {
  const val = parseFloat(amount) || 0;
  if (val >= 100000) return { value: `₹${(val / 100000).toFixed(2)}`, unit: "Lakhs  " };
  return { value: `₹${val.toFixed(2)}`, unit: "" };
};

const FIELD_OPTIONS = [
  { value: "transactionId", label: "Transaction ID", type: "string" },
  { value: "chargerId",     label: "Charger ID",     type: "string" },
  { value: "chargerName",   label: "Charger Name",   type: "string" },
  { value: "starttime",     label: "Start Time",     type: "date"   },
  { value: "endtime",       label: "End Time",       type: "date"   },
  { value: "totalKw",       label: "Energy (kWh)",   type: "number" },
  { value: "totalCost",     label: "Total Cost",     type: "number" },
];

const OPERATORS = {
  string: [
    { value: "contains",    label: "Contains"          },
    { value: "equals",      label: "Equals"            },
    { value: "startsWith",  label: "Starts With"       },
    { value: "endsWith",    label: "Ends With"         },
    { value: "isEmpty",     label: "Is Empty"          },
    { value: "isNotEmpty",  label: "Is Not Empty"      },
  ],
  number: [
    { value: "equals",             label: "Equals"                 },
    { value: "greaterThan",        label: "Greater Than"           },
    { value: "greaterThanOrEqual", label: "Greater Than or Equal"  },
    { value: "lessThan",           label: "Less Than"              },
    { value: "lessThanOrEqual",    label: "Less Than or Equal"     },
    { value: "between",            label: "Between"                },
  ],
  date: [
    { value: "after",      label: "After"        },
    { value: "before",     label: "Before"       },
    { value: "between",    label: "Between"      },
    { value: "isEmpty",    label: "Is Empty"     },
    { value: "isNotEmpty", label: "Is Not Empty" },
  ],
};

const createEmptyFilter = () => ({
  id: Date.now() + Math.random(),
  field: "",
  operator: "",
  value: "",
  valueTo: "",
});

const getFieldType = (field) =>
  FIELD_OPTIONS.find((f) => f.value === field)?.type || "string";

const applyClientFilters = (rows, searchText, filters) => {
  let result = [...rows];

  if (searchText.trim()) {
    const q = searchText.toLowerCase();
    result = result.filter(
      (r) =>
        String(r.chargerId   ?? "").toLowerCase().includes(q) ||
        String(r.chargerName ?? "").toLowerCase().includes(q) ||
        String(r.transactionId ?? "").toLowerCase().includes(q)
    );
  }

  const activeFilters = filters.filter(
    (f) =>
      f.field &&
      f.operator &&
      (f.value !== "" || ["isEmpty", "isNotEmpty"].includes(f.operator))
  );

  for (const f of activeFilters) {
    const type = getFieldType(f.field);
    result = result.filter((row) => {
      const raw = row[f.field];
      if (["isEmpty", "isNotEmpty"].includes(f.operator)) {
        const empty = raw == null || raw === "";
        return f.operator === "isEmpty" ? empty : !empty;
      }
      if (type === "string") {
        const val = String(raw ?? "").toLowerCase();
        const q   = String(f.value ?? "").toLowerCase();
        if (f.operator === "contains")   return val.includes(q);
        if (f.operator === "equals")     return val === q;
        if (f.operator === "startsWith") return val.startsWith(q);
        if (f.operator === "endsWith")   return val.endsWith(q);
      }
      if (type === "number") {
        const num = parseFloat(raw);
        const n   = parseFloat(f.value);
        const n2  = parseFloat(f.valueTo);
        if (f.operator === "equals")             return num === n;
        if (f.operator === "greaterThan")        return num > n;
        if (f.operator === "greaterThanOrEqual") return num >= n;
        if (f.operator === "lessThan")           return num < n;
        if (f.operator === "lessThanOrEqual")    return num <= n;
        if (f.operator === "between")            return num >= n && num <= n2;
      }
      if (type === "date") {
        const d   = raw ? new Date(raw).getTime() : null;
        const dF  = f.value  ? new Date(f.value).getTime()  : null;
        const dT  = f.valueTo ? new Date(f.valueTo).getTime() : null;
        if (d == null) return false;
        if (f.operator === "after")  return dF != null && d > dF;
        if (f.operator === "before") return dF != null && d < dF;
        if (f.operator === "between")
          return dF != null && dT != null && d >= dF && d <= dT;
      }
      return true;
    });
  }

  return result;
};

const describeFilter = (f) => {
  const label = FIELD_OPTIONS.find((o) => o.value === f.field)?.label ?? f.field;
  const op    = OPERATORS[getFieldType(f.field)]?.find((o) => o.value === f.operator)?.label ?? f.operator;
  const val   = f.operator === "between" ? `${f.value} – ${f.valueTo}` : f.value;
  if (["isEmpty", "isNotEmpty"].includes(f.operator)) return `${label}: ${op}`;
  return `${label} ${op} "${val}"`;
};

// ---------------------------------------------------------------------------
// Inline filter bar (matches CompactFilter visual style)
// ---------------------------------------------------------------------------

function ChargingHistoryFilter({ searchText, setSearchText, filters, setFilters }) {
  const [expanded, setExpanded] = useState(false);

  const handleAddFilter = () => {
    setFilters((prev) => [...prev, createEmptyFilter()]);
    setExpanded(true);
  };

  const handleRemoveFilter = (id) =>
    setFilters((prev) => prev.filter((f) => f.id !== id));

  const handleChange = (id, key, value) =>
    setFilters((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, [key]: value };
        if (key === "field")    { updated.operator = ""; updated.value = ""; updated.valueTo = ""; }
        if (key === "operator") { updated.value = ""; updated.valueTo = ""; }
        return updated;
      })
    );

  const handleClear = () => {
    setSearchText("");
    setFilters([]);
    setExpanded(false);
  };

  const activeCount = filters.filter(
    (f) =>
      f.field &&
      f.operator &&
      (f.value !== "" || ["isEmpty", "isNotEmpty"].includes(f.operator))
  ).length;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Top bar */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search by charger, transaction ID…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={searchFill} width={18} height={18} />
              </InputAdornment>
            ),
          }}
        />

        <Tooltip title="Add filter">
          <Button
            size="small"
            variant={expanded || activeCount > 0 ? "contained" : "outlined"}
            startIcon={<Icon icon={filterFill} width={16} height={16} />}
            onClick={() => setExpanded((v) => !v)}
            sx={{ whiteSpace: "nowrap" }}
          >
            Filters{activeCount > 0 ? ` (${activeCount})` : ""}
          </Button>
        </Tooltip>

        {(searchText || activeCount > 0) && (
          <Tooltip title="Clear all filters">
            <IconButton size="small" color="error" onClick={handleClear}>
              <Icon icon={refreshFill} width={18} height={18} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Active filter chips */}
      {activeCount > 0 && (
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            bgcolor: "primary.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Icon icon={filterFill} width={14} height={14} color="#1976d2" />
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
              Active Filters ({activeCount}):
            </Typography>
          </Stack>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {filters
              .filter(
                (f) =>
                  f.field &&
                  f.operator &&
                  (f.value !== "" || ["isEmpty", "isNotEmpty"].includes(f.operator))
              )
              .map((f) => (
                <Chip
                  key={f.id}
                  label={describeFilter(f)}
                  size="small"
                  color="primary"
                  variant="filled"
                  onDelete={() => handleRemoveFilter(f.id)}
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    height: 20,
                    "& .MuiChip-deleteIcon": { color: "white", fontSize: "0.8rem" },
                  }}
                />
              ))}
          </Box>
        </Box>
      )}

      {/* Collapsible filter builder */}
      <Collapse in={expanded}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "primary.200",
            borderRadius: 1,
            p: 2,
            bgcolor: "white",
            boxShadow: 1,
          }}
        >
          {filters.map((f, idx) => {
            const fieldType = getFieldType(f.field);
            const operators = OPERATORS[fieldType] ?? OPERATORS.string;
            const needsSecondValue = f.operator === "between";
            const noValueNeeded   = ["isEmpty", "isNotEmpty"].includes(f.operator);

            return (
              <Box
                key={f.id}
                sx={{
                  mb: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                  bgcolor: "grey.25",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Filter #{idx + 1}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => handleRemoveFilter(f.id)}>
                    <Icon icon={closeFill} width={16} height={16} />
                  </IconButton>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {/* Field selector */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={f.field}
                      label="Field"
                      onChange={(e) => handleChange(f.id, "field", e.target.value)}
                    >
                      {FIELD_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Operator selector */}
                  {f.field && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Condition</InputLabel>
                      <Select
                        value={f.operator}
                        label="Condition"
                        onChange={(e) => handleChange(f.id, "operator", e.target.value)}
                      >
                        {operators.map((op) => (
                          <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Value input(s) */}
                  {f.operator && !noValueNeeded && (
                    <>
                      <TextField
                        size="small"
                        label={needsSecondValue ? "From" : "Value"}
                        type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
                        value={f.value}
                        onChange={(e) => handleChange(f.id, "value", e.target.value)}
                        InputLabelProps={fieldType === "date" ? { shrink: true } : undefined}
                        sx={{ minWidth: 160 }}
                      />
                      {needsSecondValue && (
                        <TextField
                          size="small"
                          label="To"
                          type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
                          value={f.valueTo}
                          onChange={(e) => handleChange(f.id, "valueTo", e.target.value)}
                          InputLabelProps={fieldType === "date" ? { shrink: true } : undefined}
                          sx={{ minWidth: 160 }}
                        />
                      )}
                    </>
                  )}

                  {noValueNeeded && (
                    <Box sx={{ display: "flex", alignItems: "center", px: 2, bgcolor: "info.50", borderRadius: 1, border: "1px solid", borderColor: "info.200" }}>
                      <Typography variant="body2" color="info.main" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
                        {f.operator === "isEmpty" ? "Will find empty values" : "Will find non-empty values"}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            );
          })}

          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon={addFill} width={16} height={16} />}
            onClick={handleAddFilter}
          >
            Add Filter
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// The vehicle page shows this vehicle's sessions (matched by either battery MAC, via the
// team-scoped vehicle-charging API) for a chosen period: Today (default), Last 7 Days,
// Last 30 Days or a Custom date range. Up to 500 sessions are listed; the summary cards
// cover every session in the period.
const MAX_LISTED_SESSIONS = 500;
const PERIOD_OPTIONS = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 Days" },
  { value: "Last30Days", label: "Last 30 Days" },
  { value: "Custom", label: "Custom" },
];
const PERIOD_DAYS = { Today: 1, Last7Days: 7, Last30Days: 30 };

// Local YYYY-MM-DD for date inputs.
const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Period -> { fromTs, toTs } (UTC ISO) on local-day boundaries. Custom spans the picked
// days inclusively. Returns null for an invalid custom range.
const rangeFor = (period, customFrom, customTo) => {
  if (period === "Custom") {
    if (!customFrom || !customTo) return null;
    const from = new Date(`${customFrom}T00:00:00`);
    // Exclusive end: the next local midnight (the API compares start < to at
    // whole seconds, so 23:59:59.999 would drop the last second of the day).
    const to = new Date(`${customTo}T00:00:00`);
    to.setDate(to.getDate() + 1);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from >= to) return null;
    return { fromTs: from.toISOString(), toTs: to.toISOString() };
  }
  const now = new Date();
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - ((PERIOD_DAYS[period] || 1) - 1));
  return { fromTs: from.toISOString(), toTs: now.toISOString() };
};

export const VehicleChargingHistory = ({ vehicleId, hasBatteryMacs, batteryCapacity }) => {
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters]       = useState([]);
  const { teamId } = useSelectedTeam();
  const [period, setPeriod] = useState("Today");
  const [customFrom, setCustomFrom] = useState(() => localDateStr());
  const [customTo, setCustomTo] = useState(() => localDateStr());
  // Memoised so the query key (and "now") only change when the selection does.
  const range = useMemo(
    () => rangeFor(period, customFrom, customTo),
    [period, customFrom, customTo]
  );

  const { data: history, isFetching, isError, refetch } = useQuery(
    queryKeys.vehicleChargingHistory(teamId, {
      vehicleId,
      ...(range || {}),
      page: 1,
      limit: MAX_LISTED_SESSIONS,
    }),
    fetchWithError,
    {
      enabled: Boolean(teamId && vehicleId && range),
      select: (response) => response?.data ?? null,
    }
  );

  const periodControls = (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
    >
      {period === "Custom" && (
        <>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={customFrom}
            inputProps={{ max: customTo || localDateStr() }}
            onChange={(e) => setCustomFrom(e.target.value)}
            sx={{ width: 165 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={customTo}
            inputProps={{ min: customFrom || undefined, max: localDateStr() }}
            onChange={(e) => setCustomTo(e.target.value)}
            sx={{ width: 165 }}
          />
        </>
      )}
      <TimePeriodSelector value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
      {period === "Custom" && !range && (
        <Typography variant="caption" color="error" sx={{ width: "100%", textAlign: "right" }}>
          Choose a valid date range — From must be on or before To.
        </Typography>
      )}
    </Stack>
  );

  // Rows in the shape the table/filters below already use, plus battery + SoC.
  const data = useMemo(
    () =>
      (history?.rows || []).map((r) => ({
        transactionId: r.transactionId,
        chargerId: r.chargerId,
        chargerName: r.chargerName,
        starttime: r.startTime,
        endtime: r.stopTime,
        totalKw: r.energyKwh,
        totalCost: r.cost,
        batterySlot: r.batterySlot,
        mac: r.mac,
        startSoC: r.startSoC,
        stopSoC: r.stopSoC,
      })),
    [history]
  );
  const summary = history
    ? {
        totalEnergyKw: history.summary?.energyKwh,
        totalTransactions: history.summary?.sessions,
        totalChargingCost: history.summary?.cost,
      }
    : null;
  const isSummaryFetching = isFetching;

  const sessions  = data || [];
  const filtered  = useMemo(
    () => applyClientFilters(sessions, searchText, filters),
    [sessions, searchText, filters]
  );

  const columns = useMemo(
    () => [
      {
        field: "transactionId",
        headerName: "Transaction ID",
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) =>
          value ? (
            <Typography
              component={RouterLink}
              to={`/transactions/${value}`}
              variant="body2"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {value}
            </Typography>
          ) : (
            "-"
          ),
      },
      {
        field: "chargerId",
        headerName: "Charger ID",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "chargerName",
        headerName: "Charger Name",
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: "starttime",
        headerName: "Start Time",
        flex: 1,
        minWidth: 170,
        valueFormatter: ({ value }) => (value ? fDateTime(value) : "-"),
      },
      {
        field: "endtime",
        headerName: "End Time",
        flex: 1,
        minWidth: 170,
        valueFormatter: ({ value }) => (value ? fDateTime(value) : "-"),
      },
      {
        field: "batterySlot",
        headerName: "Battery",
        flex: 0.6,
        minWidth: 90,
        valueFormatter: ({ value }) => (value ? `Battery ${value}` : "-"),
      },
      {
        field: "totalKw",
        headerName: "Energy (kWh)",
        flex: 1,
        minWidth: 130,
        valueFormatter: ({ value }) =>
          value != null ? `${parseFloat(value).toFixed(2)} kWh` : "-",
      },
      {
        field: "startSoC",
        headerName: "Start SoC",
        flex: 0.7,
        minWidth: 100,
        valueFormatter: ({ value }) => (value != null ? `${Number(value).toFixed(0)}%` : "-"),
      },
      {
        field: "stopSoC",
        headerName: "Stop SoC",
        flex: 0.7,
        minWidth: 100,
        valueFormatter: ({ value }) => (value != null ? `${Number(value).toFixed(0)}%` : "-"),
      },
      {
        field: "totalCost",
        headerName: "Total Cost",
        flex: 1,
        minWidth: 120,
        valueFormatter: ({ value }) =>
          value != null ? `₹${parseFloat(value).toFixed(2)}` : "-",
      },
    ],
    []
  );

  if (!hasBatteryMacs) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No battery MAC address on this vehicle yet. Add Battery 1 / Battery 2 MAC on the
        Vehicle Details tab — charging sessions are matched to the vehicle by MAC.
      </Alert>
    );
  }

  // The period selector stays visible while loading / on error.
  if (isFetching) {
    return (
      <Box>
        {periodControls}
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        {periodControls}
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load charging history.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {periodControls}
      {/* Summary cards (cover every session in the selected period) */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        {[
          {
            icon: "⚡",
            label: "Total Energy Charged",
            ...(isSummaryFetching
              ? { value: "—", unit: "kWh" }
              : formatEnergy(summary?.totalEnergyKw)),
            color: "primary",
          },
          {
            icon: "🔄",
            label: "Total Sessions",
            value: isSummaryFetching ? "—" : String(summary?.totalTransactions ?? 0),
            unit: "sessions",
            color: "info",
          },
          {
            icon: "💰",
            label: "Total Charging Cost",
            ...(isSummaryFetching
              ? { value: "—", unit: "" }
              : formatAmount(summary?.totalChargingCost)),
            color: "success",
          },
          {
            icon: "🔋",
            label: "Battery Cycles Used",
            value: (() => {
              if (isSummaryFetching) return "—";
              const kw  = parseFloat(batteryCapacity);
              const tot = parseFloat(summary?.totalEnergyKw);
              if (!kw || isNaN(kw) || isNaN(tot)) return "N/A";
              return (tot / kw).toFixed(2);
            })(),
            unit: batteryCapacity && !isNaN(parseFloat(batteryCapacity)) ? "cycles" : "",
            color: "warning",
          },
        ].map(({ icon, label, value, unit, color }) => (
          <Card
            key={label}
            elevation={0}
            sx={{
              flex: "1 1 200px",
              p: 0,
              overflow: "hidden",
              border: (theme) => `1px solid ${alpha(theme.palette[color].main, 0.18)}`,
              borderRadius: 2,
              position: "relative",
              bgcolor: "background.paper",
              transition: "box-shadow 0.2s ease, transform 0.2s ease",
              "&:hover": {
                boxShadow: (theme) =>
                  `0 6px 24px ${alpha(theme.palette[color].main, 0.18)}`,
                transform: "translateY(-2px)",
              },
              /* left accent bar */
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                borderRadius: "2px 0 0 2px",
                background: (theme) =>
                  `linear-gradient(180deg, ${theme.palette[color].main} 0%, ${theme.palette[color].dark} 100%)`,
              },
            }}
          >
            <Box sx={{ pl: 3, pr: 2.5, py: 2.5 }}>
              {/* Label row */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </Typography>
              </Stack>

              {/* Value row */}
              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                <Typography
                  sx={{
                    fontSize: "1.75rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: "-0.5px",
                    color: (theme) => theme.palette[color].dark,
                  }}
                >
                  {value}
                </Typography>
                {unit && (
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      letterSpacing: 0.3,
                      color: (theme) => alpha(theme.palette[color].main, 0.75),
                      lineHeight: 1,
                    }}
                  >
                    {unit}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Card>
        ))}
      </Stack>

      {/* Table card */}
      <Card sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Charging Sessions
        </Typography>

        <ChargingHistoryFilter
          searchText={searchText}
          setSearchText={setSearchText}
          filters={filters}
          setFilters={setFilters}
        />

        {filtered.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">
              {sessions.length === 0
                ? "No charging sessions for this vehicle in the selected period."
                : "No sessions match the current filters."}
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={filtered}
            columns={columns}
            getRowId={(row) => row.transactionId}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
            sx={{ border: "none" }}
          />
        )}
      </Card>
    </Box>
  );
};
